import { useState } from "react";
import {
    Checkbox,
    Divider,
    TextField,
    Tooltip,
    Typography,
} from "@volvo/vcdk-react";
import { UITEXT } from "../../../../constants/const";
import FileUpload from "../../FileUpload/FileUpload";
import NavigationButtons from "../NavigationButtons/NavigationButtons";
import type { RequestInformationTabProps } from "../types";
import styles from "./RequestInformationTab.module.css";

const RequestInformationTab: React.FC<RequestInformationTabProps> = ({
    formData,
    handleTextChange,
    handleCheckboxChange,
    handleFileUpload,
    onNext,
    errors = {},
    isEmailVerified,
    emailVerificationError,
    onVerifyEmail,
    onRequesterEmailClick,
    uploadingFiles,
}) => {
    const handleEmailBlur = () => {
        onRequesterEmailClick?.();
        onVerifyEmail();
    };
    const [requesterUploadToolTip, setRequesterUploadToolTip] =
        useState<HTMLElement | null>(null);

    return (
        <div className={styles.RequestForm}>
            <div className={styles.Content}>
                <div className={styles.FieldWidth}>
                    <div className={styles.TextfieldWrapper}>
                        <div>
                            <TextField
                                name="requesterFirstName"
                                label="Your first name"
                                placeholder="Enter your first name"
                                value={formData.requesterFirstName}
                                onChange={handleTextChange}
                                width="100%"
                            />
                        </div>
                        <div className={styles.ErrorWrapper}>
                            {errors && errors.requesterFirstName && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.requesterFirstName}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={styles.TextfieldWrapper}>
                        <div>
                            <TextField
                                name="requesterLastName"
                                label="Your last name"
                                placeholder="Enter your last name"
                                value={formData.requesterLastName}
                                onChange={handleTextChange}
                            />
                        </div>

                        <div className={styles.ErrorWrapper}>
                            {errors && errors.requesterLastName && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.requesterLastName}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <div className={styles.TextfieldWrapper}>
                        <div>
                            <TextField
                                name="requesterEmail"
                                label="Your email"
                                placeholder="Enter your email"
                                value={formData.requesterEmail}
                                onChange={handleTextChange}
                                onBlur={handleEmailBlur}
                                disabled={isEmailVerified}
                            />
                        </div>

                        <div className={styles.ErrorWrapper}>
                            {errors && errors.requesterEmail ? (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.requesterEmail}
                                </Typography>
                            ) : emailVerificationError ? (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {emailVerificationError}
                                </Typography>
                            ) : null}
                        </div>
                    </div>
                </div>
                <Divider vertical />
                <div className={styles.FieldWidth}>
                    <div className={styles.FileUploadWrapper}>
                        <FileUpload
                            label="Upload your identity document"
                            disabled={!isEmailVerified}
                            onFileSelected={(file) => {
                                handleFileUpload(
                                    "requesterIdentityDocumentAttachment",
                                    URL.createObjectURL(file),
                                    file,
                                );
                                handleFileUpload(
                                    "requesterIdentityDocumentAttachmentName",
                                    file.name,
                                    file,
                                );
                            }}
                            onFileClear={() => {
                                handleFileUpload(
                                    "requesterIdentityDocumentAttachment",
                                    "",
                                );
                                handleFileUpload(
                                    "requesterIdentityDocumentAttachmentName",
                                    "",
                                );
                            }}
                            externalFileName={
                                formData.requesterIdentityDocumentAttachmentName
                            }
                            isUploading={uploadingFiles.has(
                                "requesterIdentityDocumentAttachment",
                            )}
                            ref={(ref) => setRequesterUploadToolTip(ref)}
                        />
                        <Tooltip
                            reference={requesterUploadToolTip}
                            offset={20}
                            id="tooltip-1"
                            variant="default"
                        >
                            Upload any identity document that is accepted as a
                            proof of identity in an EU country
                        </Tooltip>
                    </div>
                    {!isEmailVerified && (
                        <Typography variant="caption2">
                            Insert email provided for the request to enable file
                            upload.
                        </Typography>
                    )}
                    <div className={styles.ErrorWrapper}>
                        {errors &&
                            errors.requesterIdentityDocumentAttachment && (
                                <Typography
                                    variant="caption2"
                                    className={styles.ErrorMessage}
                                >
                                    {errors.requesterIdentityDocumentAttachment}
                                </Typography>
                            )}
                    </div>
                </div>
            </div>
            <Divider />
            <div className={styles.RequesterInformationTextSection}>
                <Checkbox
                    name="requesterDSA"
                    label={UITEXT.RequesterInformation.RequesterDSA}
                    checked={formData.requesterDSA}
                    onChange={handleCheckboxChange}
                    size="small"
                />
                <div className={styles.ErrorWrapper}>
                    {errors && errors.requesterDSA && (
                        <Typography
                            variant="caption2"
                            className={styles.ErrorMessage}
                        >
                            {errors.requesterDSA}
                        </Typography>
                    )}
                </div>
                <Divider />
                <Typography variant="caption2">
                    {UITEXT.RequesterInformation.ExplanationNode}
                </Typography>
            </div>
            <Divider />
            <NavigationButtons onNext={onNext} />
        </div>
    );
};

export default RequestInformationTab;
